import React, {useEffect, useMemo, useState} from "react";
import {
	Alert,
	Box,
	Button,
	ButtonBase,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import {useNavigate, useSearchParams} from "react-router-dom";
import dayjs from "dayjs";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
	PendingActionRequestKindEnum,
	PendingBoardDto,
	PendingEntry,
	PendingItemSeverityEnum,
	PendingRestControllerApiFactory,
} from "arteco-api-client-ts";
import {useApiManager} from "../../components/utils/api";
import {dateToString} from "../../components/utils/dates";
import {
	btnSx,
	EmptyState,
	FilterChips,
	ListCard,
	Page,
	PageHeader,
	Panel,
	SkeletonRows,
	SoftChip,
	StatGrid,
	StatTile,
	Tone,
} from "../../components/kit";

const SEVERITY: Record<string, { label: string, tone: Tone }> = {
	[PendingItemSeverityEnum.OVERDUE]: {label: "Vencido", tone: "error"},
	[PendingItemSeverityEnum.SOON]: {label: "Pronto", tone: "warning"},
	[PendingItemSeverityEnum.INFO]: {label: "Aviso", tone: "info"},
};

const KIND_LABEL: Record<string, string> = {
	[PendingActionRequestKindEnum.DONE]: "Hecho",
	[PendingActionRequestKindEnum.POSTPONED]: "Aplazado",
	[PendingActionRequestKindEnum.EXCEPTION]: "Excepción aceptada",
};

interface Acting {
	entry: PendingEntry;
	kind: PendingActionRequestKindEnum;
}

/**
 * Portada del admin: «Lo que pide atención» (#14020). Los pendientes de quien entra, según su rol
 * (dirección, RR. HH. y calidad, contabilidad), agrupados por fuente con su recuento; cada uno se abre
 * donde se resuelve, o se marca como hecho, se aplaza con fecha o se acepta como excepción, siempre con
 * motivo. ADMIN puede ver además todo.
 */
export const AdminPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(PendingRestControllerApiFactory);
	const navigate = useNavigate();
	const [params, setParams] = useSearchParams();
	const [all, setAll] = useState(false);
	const [data, setData] = useState<PendingBoardDto>();
	const [error, setError] = useState(false);
	const [acting, setActing] = useState<Acting>();
	const [reason, setReason] = useState("");
	const [until, setUntil] = useState(dayjs().add(7, "day").format("YYYY-MM-DD"));
	const [actError, setActError] = useState<string>();
	const [saving, setSaving] = useState(false);
	const [trigger, setTrigger] = useState(0);

	useEffect(() => {
		setError(false);
		apiManager.execute(ctrl.boardPending(all)).then(setData).catch(() => setError(true));
	}, [all, trigger]);

	const entries = data?.entries || [];
	const sources = useMemo(() => {
		const m = new Map<string, { label: string, total: number, overdue: number }>();
		entries.forEach(e => {
			const k = e.item?.source || "";
			const cur = m.get(k) || {label: e.item?.sourceLabel || k, total: 0, overdue: 0};
			cur.total++;
			if (e.item?.severity === PendingItemSeverityEnum.OVERDUE) cur.overdue++;
			m.set(k, cur);
		});
		return [...m.entries()];
	}, [data]);
	const selected = params.get("pendiente") && sources.some(([k]) => k === params.get("pendiente"))
		? params.get("pendiente") : sources[0]?.[0];
	const rows = entries.filter(e => e.item?.source === selected);
	const overdue = entries.filter(e => e.item?.severity === PendingItemSeverityEnum.OVERDUE).length;
	const soon = entries.filter(e => e.item?.severity === PendingItemSeverityEnum.SOON).length;
	const selectedLabel = sources.find(([k]) => k === selected)?.[1].label;

	const openDialog = (entry: PendingEntry, kind: PendingActionRequestKindEnum) => {
		setReason("");
		setActError(undefined);
		setUntil(dayjs().add(7, "day").format("YYYY-MM-DD"));
		setActing({entry, kind});
	};

	const save = () => {
		if (!acting) return;
		setSaving(true);
		setActError(undefined);
		apiManager.execute(ctrl.actPending({
			key: acting.entry.item?.key,
			kind: acting.kind,
			reason,
			untilDate: acting.kind === PendingActionRequestKindEnum.POSTPONED ? until : undefined,
		}))
			.then(() => {
				setActing(undefined);
				setTrigger(t => t + 1);
			})
			.catch((e: any) => setActError(e?.response?.data?.error || "No se ha podido guardar. Inténtalo de nuevo."))
			.finally(() => setSaving(false));
	};

	const subtitle = !data ? "Cargando lo que tienes pendiente…"
		: data.all ? "Todo lo pendiente de todas las áreas."
			: data.audiences?.length ? `Lo tuyo como ${data.audiences.join(", ")}, y lo que tengas a tu nombre.`
				: "Lo que tengas a tu nombre.";

	return (
		<Page>
			<PageHeader title="Lo que pide atención" subtitle={subtitle}
						actions={data?.canAll ? (
							<FilterChips options={[{value: false, label: "Lo mío"}, {value: true, label: "Todo"}]}
										 value={all} onChange={setAll}/>
						) : undefined}/>

			<StatGrid>
				<StatTile loading={!data && !error} icon={<ErrorOutlineRoundedIcon/>} tone={overdue ? "error" : "success"}
						  label="Vencidos" value={overdue}/>
				<StatTile loading={!data && !error} icon={<ScheduleRoundedIcon/>} tone={soon ? "warning" : "success"}
						  label="Vencen pronto" value={soon}/>
				<StatTile loading={!data && !error} icon={<TaskAltRoundedIcon/>} tone="info" label="Pendientes en total"
						  value={entries.length}/>
			</StatGrid>

			{error ? (
				<Panel title="Pendientes">
					<EmptyState icon={<WarningAmberRoundedIcon/>} title="No se han podido cargar los pendientes"
								description="Vuelve a cargar la página; si sigue fallando, avisa con el botón de abajo a la derecha."/>
				</Panel>
			) : !data ? (
				<Panel title="Pendientes"><SkeletonRows rows={5}/></Panel>
			) : entries.length === 0 ? (
				<Panel title="Pendientes">
					<EmptyState icon={<TaskAltRoundedIcon/>} title="Nada pendiente"
								description="Cuando algo de tu área venza o esté a punto, aparecerá aquí y en el resumen de los lunes."/>
				</Panel>
			) : (
				<>
					<Box sx={{
						display: "grid", gap: 1.5, mb: 3,
						gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))"},
					}}>
						{sources.map(([k, s]) => (
							<ButtonBase key={k} onClick={() => setParams({pendiente: k})} sx={{
								display: "flex", flexDirection: "column", alignItems: "stretch", textAlign: "left",
								p: 2, borderRadius: 2.5, border: "1px solid", bgcolor: "background.paper",
								borderColor: k === selected ? "primary.main" : "divider",
							}}>
								<Typography variant="caption" color="text.secondary" fontWeight={600}>{s.label}</Typography>
								<Typography variant="h6" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{s.total}</Typography>
								<Typography variant="caption" color={s.overdue ? "error.main" : "text.secondary"}>
									{s.overdue ? `${s.overdue} ${s.overdue === 1 ? "vencido" : "vencidos"}` : "ninguno vencido"}
								</Typography>
							</ButtonBase>
						))}
					</Box>

					<Panel title={selectedLabel || "Pendientes"} subtitle="Ábrelo donde se resuelve, o márcalo como hecho o aplázalo con su motivo.">
						<Stack spacing={1.5}>
							{rows.map(e => {
								const i = e.item!;
								const sev = SEVERITY[i.severity || PendingItemSeverityEnum.INFO];
								return (
									<ListCard key={i.key}>
										<Stack direction={{xs: "column", md: "row"}} spacing={1.5} justifyContent="space-between"
											   alignItems={{xs: "stretch", md: "center"}}>
											<Box sx={{minWidth: 0}}>
												<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
													<SoftChip tone={sev.tone} label={sev.label}/>
													{e.escalated && <SoftChip tone="neutral" label={`De ${e.forLabel}`}/>}
													{all && !e.escalated && <SoftChip tone="neutral" label={e.forLabel}/>}
												</Stack>
												<Typography variant="body2" fontWeight={600} sx={{mt: 0.75}}>{i.title}</Typography>
												<Typography variant="caption" color="text.secondary" sx={{whiteSpace: "pre-line"}}>
													{i.detail || (i.dueDate ? `Vence el ${dateToString(i.dueDate)}` : "")}
												</Typography>
											</Box>
											<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{flexShrink: 0}}>
												<Button size="small" variant="contained" sx={btnSx} endIcon={<ChevronRightRoundedIcon/>}
														onClick={() => i.link && navigate(i.link)}>Abrir</Button>
												<Button size="small" variant="outlined" sx={btnSx}
														onClick={() => openDialog(e, PendingActionRequestKindEnum.DONE)}>Hecho</Button>
												<Button size="small" variant="outlined" sx={btnSx}
														onClick={() => openDialog(e, PendingActionRequestKindEnum.POSTPONED)}>Aplazar</Button>
												<Button size="small" sx={btnSx} color="inherit"
														onClick={() => openDialog(e, PendingActionRequestKindEnum.EXCEPTION)}>Excepción</Button>
											</Stack>
										</Stack>
									</ListCard>
								);
							})}
						</Stack>
					</Panel>
				</>
			)}

			{!!data?.recentActions?.length && (
				<Panel title="Hecho, aplazado y excepciones" subtitle="Lo último que se ha hecho con los pendientes, con su motivo." sx={{mt: 3}}>
					<Stack spacing={1}>
						{data.recentActions.map(a => (
							<Box key={a.id} sx={{borderBottom: "1px solid", borderColor: "divider", pb: 1}}>
								<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
									<SoftChip tone={a.kind === PendingActionRequestKindEnum.EXCEPTION ? "warning" : "neutral"}
											  label={KIND_LABEL[a.kind as string] || a.kind}/>
									<Typography variant="body2" fontWeight={600}>{a.title}</Typography>
								</Stack>
								<Typography variant="caption" color="text.secondary" display="block">
									{a.reason}{a.untilDate ? ` · hasta el ${dateToString(a.untilDate)}` : ""} · {a.username}, {dateToString(a.created)}
								</Typography>
							</Box>
						))}
					</Stack>
				</Panel>
			)}

			<Dialog open={!!acting} onClose={() => !saving && setActing(undefined)} fullWidth maxWidth="sm">
				<DialogTitle>{acting ? KIND_LABEL[acting.kind] : ""}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{mb: 2}}>{acting?.entry.item?.title}</Typography>
					{acting?.kind === PendingActionRequestKindEnum.POSTPONED && (
						<TextField type="date" label="Hasta el" value={until} onChange={e => setUntil(e.target.value)}
								   fullWidth sx={{mb: 2}} InputLabelProps={{shrink: true}}/>
					)}
					<TextField label="Motivo" value={reason} onChange={e => setReason(e.target.value)} fullWidth multiline minRows={2}
							   helperText={acting?.kind === PendingActionRequestKindEnum.EXCEPTION
								   ? "Queda registrado: por qué se acepta no hacerlo."
								   : "Queda registrado con tu usuario y la fecha."}/>
					{actError && <Alert severity="error" sx={{mt: 2}}>{actError}</Alert>}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setActing(undefined)} disabled={saving} sx={btnSx}>Cancelar</Button>
					<Button variant="contained" onClick={save} disabled={saving || !reason.trim()} sx={btnSx}>Guardar</Button>
				</DialogActions>
			</Dialog>
		</Page>
	);
};
